/**
 * Job description text → structured HTML.
 *
 * WHY THIS EXISTS. `jobs.description` is rendered as HTML everywhere it is
 * shown — the admin edit page's TipTap editor, the approver's review page, the
 * public job detail page — but it is AUTHORED as plain text: the create wizard
 * uses a <Textarea>, the AI enhancer answers in markdown-ish prose, and ATS
 * imports arrive as whatever the source system had. HTML collapses whitespace,
 * so a perfectly structured plain-text JD arrived on screen as one unbroken
 * wall of prose with its bullet markers stranded mid-sentence
 * ("… and release. * Write clear PRDs, …").
 *
 * This converts the plain-text shapes an author actually writes — blank-line
 * paragraphs, `*`/`-`/`•` bullets, `1.` numbered steps, `#` headings, `**bold**`
 * — into the corresponding markup, and returns HTML input untouched. It
 * introduces structure only where the source text already had it: no headings
 * are guessed, no content is reordered, nothing is dropped.
 *
 * Output is markup, so callers that render it must still run their own
 * sanitizer (`sanitizeJobHtml`) — this escapes text, it is not an XSS boundary.
 */

/**
 * True when the value is already markup rather than typed text.
 *
 * Deliberately narrow: it looks for a real block/inline tag, so a JD that
 * merely mentions "<10 years" or "C++ -> Rust" is still treated as text.
 */
export function looksLikeJobDescriptionHtml(value: string): boolean {
  return /<(\/?)(p|div|ul|ol|li|h[1-6]|br|table|section|article|span|strong|em|b|i|a|img|pre|blockquote)\b[^>]*>/i.test(value);
}

function escapeText(value: string): string {
  return value.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

/** Inline markdown emphasis, applied AFTER escaping so tags cannot be injected. */
function inline(value: string): string {
  return escapeText(value)
    .replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>')
    .replace(/__([^_]+)__/g, '<strong>$1</strong>');
}

const BULLET = /^\s*(?:[*\-•·▪◦‣⁃–—]|•)\s+(.*)$/;
const NUMBERED = /^\s*\d{1,3}[.)]\s+(.*)$/;
const HEADING = /^\s*(#{1,6})\s+(.*)$/;

/**
 * Convert an authored job description to HTML, leaving existing HTML alone.
 *
 * Runs of bullet lines become one `<ul>`, runs of numbered lines one `<ol>`,
 * and consecutive prose lines one `<p>` with `<br>` between them, so the
 * author's own line breaks survive instead of being collapsed by the browser.
 */
export function jobDescriptionToHtml(value: string | null | undefined): string {
  const source = typeof value === 'string' ? value : '';
  if (!source.trim()) return '';
  if (looksLikeJobDescriptionHtml(source)) return source;

  const lines = source.replace(/\r\n?/g, '\n').split('\n');
  const blocks: string[] = [];
  let list: { tag: 'ul' | 'ol'; items: string[] } | null = null;
  let paragraph: string[] = [];

  const closeList = () => {
    if (!list) return;
    blocks.push(`<${list.tag}>${list.items.map((item) => `<li>${item}</li>`).join('')}</${list.tag}>`);
    list = null;
  };
  const closeParagraph = () => {
    if (paragraph.length === 0) return;
    blocks.push(`<p>${paragraph.join('<br>')}</p>`);
    paragraph = [];
  };
  const closeAll = () => { closeList(); closeParagraph(); };

  for (const line of lines) {
    if (!line.trim()) { closeAll(); continue; }

    const heading = HEADING.exec(line);
    if (heading) {
      closeAll();
      // Shifted down one level: the page already owns the <h1>/<h2> above it.
      const level = Math.min(heading[1].length + 1, 6);
      blocks.push(`<h${level}>${inline(heading[2].trim())}</h${level}>`);
      continue;
    }

    const bullet = BULLET.exec(line);
    const numbered = bullet ? null : NUMBERED.exec(line);
    if (bullet || numbered) {
      const tag = bullet ? 'ul' : 'ol';
      closeParagraph();
      if (list && list.tag !== tag) closeList();
      if (!list) list = { tag, items: [] };
      list.items.push(inline((bullet ?? numbered)![1].trim()));
      continue;
    }

    closeList();
    paragraph.push(inline(line.trim()));
  }
  closeAll();

  return blocks.join('');
}
